import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../../../../../../../components/ui/card";
import { BriefcaseBusiness, CalendarDays, CheckCircle2, ChevronDown, ChevronUp, Clock, Inbox, MapPin, XCircleIcon, ArrowRight } from "lucide-react";
import { Badge } from "../../../../../../../components/ui/badge";
import { Button } from "../../../../../../../components/ui/button";
import Avatar from "../../../../../../../components/shared/Avatar";
import { useScheduleContext } from "../../../..//hooks/context/useScheduleContext";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useFilters } from "../../../../../../../utils/hooks/useFilters";
import useApi from "../../../../../../../utils/api";
import { useUser } from "../../../../../../../utils/hooks/user";
import { useCompanyTimeFormat } from "../../../../../../../utils/hooks/company";
import { calculateSections } from "../../../../hooks/form/useCalculatedSections";
import { renderSectionsDuration } from "../../../../helpers/eventdisplay/renderSectionsDuration";
import { renderDuration } from "../../../../helpers/eventdisplay/renderDuration";
import { firstToUpper } from "../../../../../../../utils/helpers/string";
import { renderSettingsResourceLabel } from "../../../../../../../utils/helpers/settings";
import { renderUserName } from "../../../../../../../utils/helpers/user";
import { cn } from "../../../../../../../lib/utils";

const options: string[] = ["all", "pending", "approved", "denied"];


function AvailableShifts({ approve, deny }: any) {
    const { t } = useTranslation();
    const user = useUser();
    const companyTimeFormat = useCompanyTimeFormat();
    const navigate = useNavigate();
    const [tab, setTab] = useState<string>("all");
    const [open, setOpen] = useState<Record<string, boolean>>({});
    const [filters] = useFilters();
    const { type } = useScheduleContext();
    const { authGet } = useApi();

    const { canApproveRequest, canDenyRequest } = usePermissions();

    const params = useMemo(() => {
        let values: any = {
            startDate: filters?.mode === "month"
                ? moment(filters?.from).startOf("week").format("YYYY-MM-DD")
                : filters?.from,
            endDate: filters?.mode === "month"
                ? moment(filters?.to).endOf("week").format("YYYY-MM-DD")
                : filters?.to
        }

        if (type === "home") {
            values.isMySchedule = true;
        }

        return values;
    }, [filters?.from, filters?.to, filters?.mode, type]);

    const query = useQuery({
        queryKey: ["/request/my-team/offer-shifts-statistics", params],
        queryFn: ({ queryKey }: any) => {
            const [url, params] = queryKey;
            return authGet(url, { params });
        }
    });

    const data: any = useMemo(() => {
        const source = query.data || {};

        const approved = Number(source?.approved?.count || 0);
        const pending = Number(source?.pending?.count || 0);
        const denied = Number(source?.denied?.count || 0);

        const total = approved + pending + denied;

        const sortByStartDateDesc = (a: any, b: any) =>
            moment(b?.data?.startDate).valueOf() -
            moment(a?.data?.startDate).valueOf();

        const allItems = [
            ...(source?.approved?.items || []),
            ...(source?.pending?.items || []),
            ...(source?.denied?.items || []),
        ].sort(sortByStartDateDesc).slice(0, 5);

        return {
            data: tab === "all" ? allItems : source?.[tab]?.items || [],
            all: total,
            approved,
            pending,
            denied,
        };
    }, [query.data, tab]);

    const statusStyles: Record<string, string> = {
        pending: "tw-bg-amber-50 tw-text-amber-600",
        approved: "tw-bg-green-50 tw-text-green-600",
        denied: "tw-bg-red-50 tw-text-red-500",
    };

    return (
        <Card className="tw-w-full tw-h-full tw-rounded-xl tw-border-gray-200">
            <CardHeader className="tw-pb-0">
                <CardTitle className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-3">
                    {firstToUpper(t("offer-shift"))}
                </CardTitle>
                <div className="tw-flex tw-border-b tw-border-gray-200">
                    {options.map((option) => (
                        <button
                            key={option}
                            type="button"
                            onClick={() => setTab(option)}
                            className={cn(
                                "tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium -tw-mb-px tw-border-b-2 tw-transition-colors",
                                tab === option
                                    ? "tw-border-primary tw-text-primary"
                                    : "tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700"
                            )}
                        >
                            {t(option)}
                            <span className={cn(
                                "tw-text-[10px] tw-font-semibold tw-rounded-full tw-px-1.5 tw-py-0.5",
                                tab === option ? "tw-bg-primary/10 tw-text-primary" : "tw-bg-gray-100 tw-text-gray-500"
                            )}>
                                {data?.[option]}
                            </span>
                        </button>
                    ))}
                </div>
            </CardHeader>

            <CardContent className="tw-relative tw-h-full tw-pt-4">
                {data?.[tab] === 0 ? (
                    <div className="tw-overflow-y-auto tw-h-[18rem]">
                        <div className="tw-text-sm tw-text-neutral-500 tw-flex tw-flex-col tw-items-center tw-border tw-border-dashed tw-border-gray-200 tw-rounded-lg tw-py-8">
                            <Inbox className="tw-mb-2 tw-opacity-20" />
                            {t("no-data")}
                        </div>
                    </div>
                ) : (
                    <div className="tw-overflow-y-auto tw-h-[17.5rem] tw-mb-2 tw-space-y-2">
                        {data?.data?.map((shift: any) => {
                            const scheduleData = shift?.data?.schedule || {};
                            const originalUser = shift?.data?.originalUser;
                            const offerUser = shift?.data?.offerUser;
                            const sections = calculateSections(scheduleData?.sections || [], shift?.start * 1000);

                            const totalBreaksDuration = sections?.reduce((total: number, section: any) => {
                                if (section.type === "break" && section.start && section.end) {
                                    return total + moment(section.end).diff(moment(section.start), "seconds");
                                }
                                return total;
                            }, 0);

                            const isApprover = (shift?.requiredToBeApprovedBy || []).includes(user?.id) || shift?.requestType === "open-shift" || shift?.requestType === "call-off";
                            const isPending = shift.status === "pending";

                            return (
                                <div key={shift.id} className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-3">
                                    <div className="tw-flex tw-items-center tw-justify-between">
                                        <div className="tw-flex tw-items-center tw-flex-1 tw-min-w-0 tw-text-sm">
                                            <div className="tw-flex tw-items-center tw-gap-1.5">
                                                <Avatar user={originalUser} size="sm" className="tw-border-2 tw-border-background" />
                                                <span className="tw-truncate">{renderUserName(originalUser)}</span>
                                            </div>
                                            <div className="tw-flex tw-items-center tw-gap-1.5 tw-font-medium tw-truncate">
                                                <ArrowRight size={12} className="tw-mx-1.5 tw-shrink-0 tw-text-gray-300" />
                                                <Avatar user={offerUser} size="sm" className="tw-border-2 tw-border-background" />
                                                <span className="tw-truncate">{renderUserName(offerUser)}</span>
                                            </div>
                                        </div>

                                        <div className="tw-flex tw-gap-1.5 tw-items-center tw-shrink-0">
                                            {isPending && (canDenyRequest || isApprover) && (
                                                <XCircleIcon
                                                    size={17}
                                                    className="tw-text-rose-500 tw-cursor-pointer hover:tw-opacity-80"
                                                    onClick={() => deny({ ...shift, close: query.refetch })}
                                                />
                                            )}
                                            {isPending && (canApproveRequest || isApprover) && (
                                                <CheckCircle2
                                                    size={17}
                                                    className="tw-text-primary tw-cursor-pointer hover:tw-opacity-80"
                                                    onClick={() => approve({ ...shift, close: query.refetch })}
                                                />
                                            )}
                                            <button
                                                onClick={() => setOpen(prev => ({ ...prev, [shift.id]: !prev[shift.id] }))}
                                                className="tw-text-gray-400 hover:tw-text-gray-600 tw-cursor-pointer"
                                            >
                                                {open[shift.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                            </button>
                                        </div>
                                    </div>

                                    {!open[shift.id] && (
                                        <div className="tw-mt-1.5 tw-flex tw-items-center tw-gap-2 tw-text-[11px] tw-text-gray-400">
                                            <span>{moment.parseZone(scheduleData?.date).format("MMM DD")}</span>
                                            <span>·</span>
                                            <span>
                                                {moment.parseZone(scheduleData.startDate).format(companyTimeFormat)}
                                                {" – "}
                                                {moment.parseZone(scheduleData.endDate).format(companyTimeFormat)}
                                            </span>
                                            <span className={cn("tw-ml-auto tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium", statusStyles[shift.status])}>
                                                {t(shift.status)}
                                            </span>
                                        </div>
                                    )}

                                    {open[shift.id] && (
                                        <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-gray-100 tw-space-y-2">
                                            <div className="tw-flex tw-items-center tw-gap-3">
                                                <CalendarDays size={14} className="tw-text-primary tw-shrink-0" />
                                                <span className="tw-text-xs tw-text-gray-700">{moment.parseZone(scheduleData.date).format("ddd, DD MMMM YYYY")}</span>
                                            </div>

                                            <div className="tw-flex tw-items-center tw-justify-between">
                                                <div className="tw-flex tw-items-center tw-gap-3">
                                                    <Clock size={14} className="tw-text-primary tw-shrink-0" />
                                                    <span className="tw-text-xs tw-text-gray-700">
                                                        {moment.parseZone(scheduleData.startDate).format(companyTimeFormat)} – {moment.parseZone(scheduleData.endDate).format(companyTimeFormat)}
                                                    </span>
                                                </div>
                                                <div className="tw-flex tw-items-center tw-gap-1">
                                                    <Badge variant="secondary" className="tw-text-xs">
                                                        {renderSectionsDuration(moment(scheduleData.endDate).diff(moment(scheduleData.startDate), "seconds"), sections)}
                                                    </Badge>
                                                    {totalBreaksDuration > 0 && (
                                                        <Badge className="tw-text-xs tw-bg-orange-100 tw-text-orange-600 tw-border-0">
                                                            {t("breaks")}: {renderDuration(totalBreaksDuration)}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>

                                            {shift?.data?.job && (
                                                <div className="tw-flex tw-items-center tw-justify-between">
                                                    <div className="tw-flex tw-items-center tw-gap-3 tw-text-gray-400">
                                                        <BriefcaseBusiness size={14} />
                                                        <span className="tw-text-xs">{t("job")}</span>
                                                    </div>
                                                    <span className="tw-text-xs tw-font-medium tw-text-gray-700">{renderSettingsResourceLabel(shift?.data?.job)}</span>
                                                </div>
                                            )}
                                            {shift?.data?.location && (
                                                <div className="tw-flex tw-items-center tw-justify-between">
                                                    <div className="tw-flex tw-items-center tw-gap-3 tw-text-gray-400">
                                                        <MapPin size={14} />
                                                        <span className="tw-text-xs">{t("location")}</span>
                                                    </div>
                                                    <span className="tw-text-xs tw-font-medium tw-text-gray-700">{renderSettingsResourceLabel(shift?.data?.location)}</span>
                                                </div>
                                            )}
                                            {shift.data?.reason && (
                                                <div className="tw-text-xs tw-bg-gray-50 tw-border tw-border-gray-100 tw-p-2 tw-rounded tw-italic tw-text-gray-500">
                                                    "{shift.data.reason}"
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                <Button
                    variant="ghost"
                    className="tw-w-full tw-text-xs tw-text-primary hover:tw-text-primary"
                    onClick={() => navigate(`/request/schedule?menu=offer-shifts`)}
                >
                    {t("view-all-open-shifts")}
                </Button>
            </CardContent>
        </Card>
    );
}

export default AvailableShifts;
